// React Query mutation hooks for the Library publish/fork flow.
// Both call SECURITY-DEFINER edge functions; the hooks just wrap invoke()
// with toast feedback and cache invalidation.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { LibraryKind, LibraryEntry } from "@/hooks/useLibrary";
import { buildIdempotencyKey } from "@/lib/idempotency";

export interface PublishInput {
  kind: LibraryKind;
  source_id: string;
  squad_id?: string | null;
  title?: string;
  summary?: string;
  tags?: string[];
  visibility?: "squad" | "public";
  /** Optional client-supplied stable key. If omitted we derive one from
   *  (kind, source_id, visibility, squad_id) so retries of the same logical
   *  publish never create a duplicate row even mid-flight. */
  idempotency_key?: string;
}

export const usePublishToLibrary = () => {
  const qc = useQueryClient();
  return useMutation<{ ok: true; entry: LibraryEntry }, Error, PublishInput>({
    mutationFn: async (input: PublishInput) => {
      // Auto-derive an idempotency key when the caller didn't supply one.
      // Same kind + source_id + visibility + squad_id means "the same
      // publish" — which is exactly what we want retries to collapse to.
      const idempotencyKey =
        input.idempotency_key ||
        (await buildIdempotencyKey(
          "library_publish",
          input.kind,
          input.source_id,
          input.visibility ?? "squad",
          input.squad_id ?? "",
        ));
      const { data, error } = await supabase.functions.invoke("library-publish", {
        body: { ...input, idempotency_key: idempotencyKey },
        headers: { "Idempotency-Key": idempotencyKey },
      });
      if (error) throw new Error(error.message ?? "Publish failed");
      if (data?.error) throw new Error(data.message ?? data.error);
      return data as { ok: true; entry: LibraryEntry };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["library"] });
      toast.success("Published to the Library");
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Publish failed"),
  });
};

export interface ForkInput {
  entry_id: string;
  target_squad_id?: string | null;
}

export const useForkLibraryEntry = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ForkInput) => {
      const { data, error } = await supabase.functions.invoke("library-fork", {
        body: input,
      });
      if (error) throw new Error(error.message ?? "Fork failed");
      if (data?.error) throw new Error(data.message ?? data.error);
      return data as {
        ok: true;
        resource_kind: string;
        resource_id: string;
        redirect: string;
        /** null = nothing to record; false = had skills but couldn't credit them; true = credited. */
        skills_recorded?: boolean | null;
        skills_tagged?: number;
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["library"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Fork failed"),
  });
};
